import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LayoutDashboardIcon, MapPinIcon, ClipboardListIcon, DollarSignIcon, ShoppingCartIcon, FileTextIcon, UsersIcon, MapIcon, LogOutIcon, XIcon, ShieldCheckIcon, BuildingIcon, SettingsIcon, ScrollTextIcon, ReceiptIcon, PackageIcon, BoxIcon, TruckIcon, CalendarDaysIcon, FileOutputIcon, RotateCcwIcon, TagIcon, PercentIcon, ClipboardPlusIcon, PhoneIcon, ChevronDownIcon } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Permissions } from '../../types';
interface SidebarProps {
  currentPage: string;
  onNavigate: (page: string) => void;
  isOpen: boolean;
  onToggle: () => void;
  mobileMode?: boolean;
}
interface NavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  permission: keyof Permissions | 'settings' | 'bitacora' | 'cuentas_cobrar' | 'pedidos' | 'inventario' | 'caja_vacia' | 'facturas_dejadas' | 'devoluciones' | 'ofertas' | 'comisiones' | 'conduces' | 'compras' | 'call_center';
}
// Group definitions
const navGroups = [{
  label: 'GESTIÓN',
  items: ['dashboard', 'visits', 'new-visit', 'planificacion']
}, {
  label: 'COMERCIAL',
  items: ['collections', 'sales', 'pedidos', 'ofertas', 'comisiones', 'call-center']
}, {
  label: 'OPERACIONES',
  items: ['cuentas-cobrar', 'conduces', 'facturas-dejadas', 'devoluciones', 'compras', 'inventario', 'caja-vacia']
}, {
  label: 'ANÁLISIS',
  items: ['clients', 'reports', 'map']
}, {
  label: 'SISTEMA',
  items: ['users', 'roles', 'bitacora']
}];
const navItems: NavItem[] = [{
  id: 'dashboard',
  label: 'Panel Principal',
  icon: <LayoutDashboardIcon className="w-5 h-5" data-id="element-161" />,
  permission: 'dashboard'
}, {
  id: 'visits',
  label: 'Mis Visitas',
  icon: <ClipboardListIcon className="w-5 h-5" data-id="element-162" />,
  permission: 'visits'
}, {
  id: 'new-visit',
  label: 'Nueva Visita',
  icon: <MapPinIcon className="w-5 h-5" data-id="element-163" />,
  permission: 'new-visit'
}, {
  id: 'planificacion',
  label: 'Planificación',
  icon: <CalendarDaysIcon className="w-5 h-5" data-id="element-164" />,
  permission: 'planificacion'
}, {
  id: 'collections',
  label: 'Cobros',
  icon: <DollarSignIcon className="w-5 h-5" data-id="element-165" />,
  permission: 'collections'
}, {
  id: 'sales',
  label: 'Ventas',
  icon: <ShoppingCartIcon className="w-5 h-5" data-id="element-166" />,
  permission: 'sales'
}, {
  id: 'pedidos',
  label: 'Pedidos',
  icon: <TruckIcon className="w-5 h-5" data-id="element-167" />,
  permission: 'pedidos'
}, {
  id: 'ofertas',
  label: 'Ofertas e Impulsos',
  icon: <TagIcon className="w-5 h-5" data-id="element-168" />,
  permission: 'ofertas'
}, {
  id: 'comisiones',
  label: 'Comisiones',
  icon: <PercentIcon className="w-5 h-5" data-id="element-169" />,
  permission: 'comisiones'
}, {
  id: 'conduces',
  label: 'Conduces',
  icon: <TruckIcon className="w-5 h-5" data-id="element-170" />,
  permission: 'conduces'
}, {
  id: 'call-center',
  label: 'Call Center',
  icon: <PhoneIcon className="w-5 h-5" data-id="element-171" />,
  permission: 'call_center'
}, {
  id: 'cuentas-cobrar',
  label: 'Cuentas x Cobrar',
  icon: <ReceiptIcon className="w-5 h-5" data-id="element-172" />,
  permission: 'cuentas_cobrar'
}, {
  id: 'facturas-dejadas',
  label: 'Facturas Depositadas',
  icon: <FileOutputIcon className="w-5 h-5" data-id="element-173" />,
  permission: 'facturas_dejadas'
}, {
  id: 'devoluciones',
  label: 'Devoluciones',
  icon: <RotateCcwIcon className="w-5 h-5" data-id="element-174" />,
  permission: 'devoluciones'
}, {
  id: 'compras',
  label: 'Compras',
  icon: <ClipboardPlusIcon className="w-5 h-5" data-id="element-175" />,
  permission: 'compras'
}, {
  id: 'inventario',
  label: 'Inventario',
  icon: <PackageIcon className="w-5 h-5" data-id="element-176" />,
  permission: 'inventario'
}, {
  id: 'caja-vacia',
  label: 'Caja Vacía',
  icon: <BoxIcon className="w-5 h-5" data-id="element-177" />,
  permission: 'caja_vacia'
}, {
  id: 'clients',
  label: 'Farmacias',
  icon: <BuildingIcon className="w-5 h-5" data-id="element-178" />,
  permission: 'clients'
}, {
  id: 'reports',
  label: 'Reportes',
  icon: <FileTextIcon className="w-5 h-5" data-id="element-179" />,
  permission: 'reports'
}, {
  id: 'map',
  label: 'Mapa GPS',
  icon: <MapIcon className="w-5 h-5" data-id="element-180" />,
  permission: 'map'
}, {
  id: 'users',
  label: 'Usuarios',
  icon: <UsersIcon className="w-5 h-5" data-id="element-181" />,
  permission: 'users'
}, {
  id: 'roles',
  label: 'Roles y Permisos',
  icon: <ShieldCheckIcon className="w-5 h-5" data-id="element-182" />,
  permission: 'roles'
}, {
  id: 'bitacora',
  label: 'Bitácora',
  icon: <ScrollTextIcon className="w-5 h-5" data-id="element-183" />,
  permission: 'bitacora'
}];
export function Sidebar({
  currentPage,
  onNavigate,
  isOpen,
  onToggle,
  mobileMode = false
}: SidebarProps) {
  const {
    user,
    logout
  } = useAuth();
  const filteredItems = navItems.filter(item => {
    if (!user) return false;
    if (item.permission === 'clients') {
      return user.rolNombre === 'Administrador' || user.permisos?.users === true || user.permisos?.clients === true;
    }
    if (item.permission === 'bitacora') {
      return user.rolNombre === 'Administrador' || user.permisos?.bitacora === true;
    }
    if (['cuentas_cobrar', 'pedidos', 'inventario', 'caja_vacia', 'facturas_dejadas', 'devoluciones', 'ofertas', 'comisiones', 'conduces', 'compras', 'planificacion', 'descuento_pronto_pago', 'sales', 'inventario_editar', 'pedidos_editar', 'call_center'].includes(item.permission)) {
      const permKey = item.permission as keyof Permissions;
      return user.rolNombre === 'Administrador' || user.permisos?.[permKey] === true;
    }
    return user.permisos[item.permission as keyof Permissions] === true;
  });
  // ── Mobile Bottom Sheet ──────────────────────────────────────────────
  if (mobileMode) {
    return <AnimatePresence data-id="element-184">
        {isOpen && <>
            {/* Backdrop */}
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.2
        }} className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50" onClick={onToggle} data-id="element-185" />

            {/* Bottom Sheet */}
            <motion.div initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '100%'
        }} transition={{
          type: 'spring',
          damping: 28,
          stiffness: 300
        }} className="fixed inset-x-0 bottom-0 z-50 bg-white rounded-t-3xl shadow-2xl" style={{
          maxHeight: '85vh',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)'
        }} data-id="element-186">
              {/* Drag handle */}
              <div className="flex justify-center pt-3 pb-2" data-id="element-187">
                <div className="w-10 h-1 rounded-full bg-slate-300" data-id="element-188" />
              </div>

              {/* Header */}
              <div className="flex items-center justify-between px-5 pb-3 border-b border-slate-100" data-id="element-189">
                <div className="flex items-center gap-3" data-id="element-190">
                  <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center shadow-sm border border-slate-100 overflow-hidden p-1" data-id="element-191">
                    <img src="https://cdn.magicpatterns.com/uploads/x7GeSJnrf7zHwSFqA8Zq1u/Captura_de_pantalla_2026-02-19_124313-removebg-preview.png" alt="MR Logo" className="w-full h-full object-contain" data-id="element-192" />
                  </div>
                  <div data-id="element-193">
                    <p className="text-sm font-bold text-slate-900" data-id="element-194">Menú</p>
                    <p className="text-[11px] text-slate-500" data-id="element-195">{user?.nombre}</p>
                  </div>
                </div>
                <button onClick={onToggle} className="p-2 hover:bg-slate-100 rounded-xl text-slate-400 transition-colors" data-id="element-196">
                  <XIcon className="w-5 h-5" data-id="element-197" />
                </button>
              </div>

              {/* Nav items */}
              <div className="overflow-y-auto px-4 py-4 space-y-5" style={{
            maxHeight: 'calc(85vh - 140px)'
          }} data-id="element-198">
                {navGroups.map(group => {
              const groupItems = filteredItems.filter(item => group.items.includes(item.id));
              if (groupItems.length === 0) return null;
              return <div key={group.label} data-id="element-199">
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2 px-1" data-id="element-200">
                        {group.label}
                      </p>
                      <div className="grid grid-cols-3 gap-2" data-id="element-201">
                        {groupItems.map(item => {
                    const isActive = currentPage === item.id;
                    return <button key={item.id} onClick={() => onNavigate(item.id)} className={`flex flex-col items-center gap-1.5 p-3 rounded-xl text-center transition-all min-h-[72px] justify-center ${isActive ? 'bg-[#2952C4] text-white shadow-md shadow-blue-900/20' : 'bg-slate-50 text-slate-700 hover:bg-slate-100 active:bg-slate-200'}`} data-id="element-202">
                              <span className={isActive ? 'text-white' : 'text-slate-500'} data-id="element-203">
                                {item.icon}
                              </span>
                              <span className="text-[11px] font-semibold leading-tight" data-id="element-204">
                                {item.label}
                              </span>
                            </button>;
                  })}
                      </div>
                    </div>;
            })}
              </div>

              {/* Bottom actions */}
              <div className="border-t border-slate-100 px-4 py-3 flex gap-2" data-id="element-205">
                <button onClick={() => onNavigate('settings')} className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold transition-colors ${currentPage === 'settings' ? 'bg-[#2952C4] text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`} data-id="element-206">
                  <SettingsIcon className="w-4 h-4" data-id="element-207" />
                  Configuración
                </button>
                <button onClick={logout} className="flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-sm font-semibold bg-rose-50 text-rose-600 hover:bg-rose-100 transition-colors" data-id="element-208">
                  <LogOutIcon className="w-4 h-4" data-id="element-209" />
                  Salir
                </button>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>;
  }
  // ── Desktop Sidebar (unchanged) ──────────────────────────────────────
  return <aside className="fixed top-0 left-0 z-50 h-screen w-64 flex flex-col lg:sticky lg:top-0 lg:z-auto lg:translate-x-0 shadow-2xl" style={{
    background: 'linear-gradient(180deg, #0d1332 0%, #111a4a 100%)'
  }} data-id="element-210">
      {/* Logo area */}
      <div className="flex items-center justify-between h-20 px-5 border-b border-white/10 flex-shrink-0 bg-[#0d1332]/50 backdrop-blur-sm" data-id="element-211">
        <button onClick={() => onNavigate('dashboard')} className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer hover:opacity-80 transition-opacity" data-id="element-212">
          <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-lg shadow-blue-900/50 flex-shrink-0 overflow-hidden p-1.5" data-id="element-213">
            <img src="https://cdn.magicpatterns.com/uploads/x7GeSJnrf7zHwSFqA8Zq1u/Captura_de_pantalla_2026-02-19_124313-removebg-preview.png" alt="MR Logo" className="w-full h-full object-contain" data-id="element-214" />
          </div>
          <div className="min-w-0 text-left" data-id="element-215">
            <p className="text-white font-bold text-sm leading-tight truncate font-heading" data-id="element-216">
              Miladys Rodríguez
            </p>
            <p className="text-[11px] font-medium truncate opacity-80" style={{
            color: '#aac1ef'
          }} data-id="element-217">
              & Asociados
            </p>
          </div>
        </button>
      </div>

      <nav className="flex-1 px-3 py-4 space-y-6 overflow-y-auto custom-scrollbar" data-id="element-218">
        {navGroups.map(group => {
        const groupItems = filteredItems.filter(item => group.items.includes(item.id));
        if (groupItems.length === 0) return null;
        return <div key={group.label} className="space-y-1" data-id="element-219">
              <div className="px-3 mb-2" data-id="element-220">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider opacity-70" data-id="element-221">
                  {group.label}
                </p>
              </div>
              {groupItems.map(item => {
            const isActive = currentPage === item.id;
            return <button key={item.id} onClick={() => onNavigate(item.id)} className={`
                      w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium
                      transition-all duration-200 relative group
                      ${isActive ? 'text-white shadow-lg shadow-blue-900/40' : 'text-slate-300 hover:bg-white/10 hover:text-white'}
                    `} style={isActive ? {
              background: 'linear-gradient(90deg, #2D3A8C 0%, #3B4BC8 100%)'
            } : {}} data-id="element-222">
                    <span className={`transition-colors duration-200 ${isActive ? 'text-white' : 'text-slate-400 group-hover:text-white'}`} data-id="element-223">
                      {item.icon}
                    </span>
                    {item.label}
                    {isActive && <div className="absolute right-3 w-1.5 h-1.5 rounded-full bg-blue-300 shadow-[0_0_8px_rgba(147,197,253,0.6)]" data-id="element-224" />}
                  </button>;
          })}
            </div>;
      })}
      </nav>

      {/* Bottom actions */}
      <div className="p-3 border-t border-white/10 flex-shrink-0 space-y-1 bg-[#0d1332]/30 backdrop-blur-md" data-id="element-225">
        <button onClick={() => onNavigate('settings')} className={`
            w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium
            transition-all duration-200 relative
            ${currentPage === 'settings' ? 'text-white shadow-lg shadow-blue-900/40' : 'text-slate-300 hover:bg-white/10 hover:text-white'}
          `} style={currentPage === 'settings' ? {
        background: 'linear-gradient(90deg, #2D3A8C 0%, #3B4BC8 100%)'
      } : {}} data-id="element-226">
          <SettingsIcon className="w-5 h-5" data-id="element-227" />
          Configuración
        </button>
        <button onClick={logout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-slate-300 hover:bg-rose-500/20 hover:text-rose-200 transition-all duration-200" data-id="element-228">
          <LogOutIcon className="w-5 h-5" data-id="element-229" />
          Cerrar Sesión
        </button>
      </div>
    </aside>;
}